import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getUser } from '../lib/storage';
import { resolveColor } from '../theme/engine';
import {
  Branding,
  PRODUCT_BRANDING,
  applyBranding,
  cachedTenantBranding,
  fetchTenantBranding,
  mergeBranding,
} from '../services/branding';

interface BrandingContextValue {
  branding: Branding;
  /** Re-read the current tenant's branding, e.g. after saving it. */
  refresh: () => Promise<void>;
  /**
   * Show a specific tenant's branding regardless of who is signed in. Used by
   * the public chat widget, whose visitors are not signed in; null goes back
   * to the signed-in user's tenant.
   */
  showTenant: (tenantId: string | null) => void;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: PRODUCT_BRANDING,
  refresh: async () => undefined,
  showTenant: () => undefined,
});

function signedInTenantId(user: any): string | null {
  const stored: any = user ?? getUser();
  return stored?.tenantId || stored?.user?.tenantId || stored?.tenant?.id || null;
}

/**
 * Provides the branding in effect and applies it to the page.
 *
 * The tenant is the signed-in user's (or the one a public page asked for). The
 * cached copy for that tenant is applied immediately so there is no flash of
 * the product's colours, then refreshed from the server.
 */
export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [override, setOverride] = useState<string | null>(null);
  const tenantId = override ?? signedInTenantId(user);
  const [branding, setBranding] = useState<Branding>(PRODUCT_BRANDING);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!tenantId) {
        setBranding(PRODUCT_BRANDING);
        return;
      }

      const cached = cachedTenantBranding(tenantId);
      if (cached) {
        const merged = await mergeBranding(cached);
        if (!signal?.aborted) setBranding(merged);
      }

      const fresh = await fetchTenantBranding(tenantId, signal);
      if (fresh && !signal?.aborted) setBranding(await mergeBranding(fresh));
      // On failure the cached copy (or the product's branding) stays in place.
    },
    [tenantId]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    applyBranding(branding);
  }, [branding]);

  const value = useMemo<BrandingContextValue>(
    () => ({ branding, refresh: () => load(), showTenant: setOverride }),
    [branding, load]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = (): BrandingContextValue => useContext(BrandingContext);

/** The product name to show: the client's, else the product's. */
export const useProductName = (): string => useBranding().branding.productName;

/**
 * Themed value of one of today's colours, for places that need a real colour
 * rather than a class (chart props, SVG attributes).
 */
export function useThemeColor(): (hex: string) => string {
  const { branding } = useBranding();
  return useCallback((hex: string) => resolveColor(hex, branding.colors), [branding.colors]);
}
